import { zodResolver } from '@hookform/resolvers/zod'
import { useQuery } from '@tanstack/react-query'
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router'
import { useState } from 'react'
import { useForm, useWatch } from 'react-hook-form'
import { z } from 'zod'
import { AuthShell } from '@/components/auth-shell'
import { PasswordChecklist } from '@/components/password-checklist'
import { passwordRequirements } from '@/lib/password-policy'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { authApi } from '@/lib/auth-api'

export const Route = createFileRoute('/account/password/reset/key/$key')({
  component: PasswordResetKeyPage,
})

const resetSchema = z
  .object({
    password: z
      .string()
      .refine(
        (value) => passwordRequirements.every((r) => r.test(value)),
        'Hasło nie spełnia wymagań',
      ),
    confirm: z.string(),
  })
  .refine((data) => data.password === data.confirm, {
    message: 'Hasła muszą być identyczne',
    path: ['confirm'],
  })

function PasswordResetKeyPage() {
  const { key } = Route.useParams()
  const navigate = useNavigate()
  const [resetError, setResetError] = useState<string | null>(null)

  const keyCheck = useQuery({
    queryKey: ['auth', 'password-reset-key', key],
    queryFn: () => authApi.getPasswordReset(key),
    retry: false,
    staleTime: Infinity,
  })

  const form = useForm({
    resolver: zodResolver(resetSchema),
    defaultValues: { password: '', confirm: '' },
  })
  const password = useWatch({ control: form.control, name: 'password' })

  const submit = form.handleSubmit(async ({ password }) => {
    setResetError(null)
    const response = await authApi.resetPassword(key, password)
    if (response.status === 429) {
      setResetError('Zbyt wiele prób. Spróbuj ponownie później.')
      return
    }
    if (response.errors?.length) {
      setResetError(response.errors[0].message)
      return
    }
    navigate({ to: '/login', search: { reset: 'ok' } })
  })

  const keyInvalid = keyCheck.data && keyCheck.data.status !== 200

  return (
    <AuthShell>
      <Card>
        {keyCheck.isPending ? (
          <CardContent className="py-10 text-center text-sm text-muted-foreground">
            Sprawdzanie linku…
          </CardContent>
        ) : keyInvalid ? (
          <CardContent className="flex flex-col gap-4 py-10 text-center text-sm">
            {/* TR-121: jeden komunikat dla wszystkich nieprawidłowych stanów tokena. */}
            <p>Link jest nieprawidłowy lub wygasł. Ponów żądanie resetu hasła.</p>
            <Link
              to="/account/password/reset"
              className="text-muted-foreground underline-offset-4 hover:underline"
            >
              Ponów żądanie resetu
            </Link>
          </CardContent>
        ) : (
          <>
            <CardHeader>
              <CardTitle>Ustaw nowe hasło</CardTitle>
              <CardDescription>Wpisz nowe hasło do swojego konta.</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={submit} className="flex flex-col gap-4" noValidate>
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="password">Nowe hasło</Label>
                  <Input
                    id="password"
                    type="password"
                    autoComplete="new-password"
                    autoFocus
                    {...form.register('password')}
                  />
                  <PasswordChecklist password={password} />
                </div>
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="confirm">Powtórz hasło</Label>
                  <Input
                    id="confirm"
                    type="password"
                    autoComplete="new-password"
                    {...form.register('confirm')}
                  />
                  {form.formState.errors.confirm && (
                    <p className="text-sm text-destructive">
                      {form.formState.errors.confirm.message}
                    </p>
                  )}
                </div>
                {resetError && <p className="text-sm text-destructive">{resetError}</p>}
                <Button type="submit" disabled={form.formState.isSubmitting}>
                  Ustaw hasło
                </Button>
              </form>
            </CardContent>
          </>
        )}
      </Card>
    </AuthShell>
  )
}
